import React from 'react';
import { NeuroMessage, useProduct } from '@ignitesales/neuro-application/src/neuro-guide';
import { useNeuroMessages } from '@ignitesales/neuro-application/src/neuro-guide/components';
import ReactSVG from 'react-svg';
import PropTypes from 'prop-types';
import Collapsible from 'react-collapsible';

const Product = ({ product, summary, isDetails = false, isExisting = false , isAddOn = false}) => {
  const {
    description,
    disclosures,
    descriptionAbbreviated,
    features,
    name,
    url,
    onProductActionClick
  } = useProduct(product);

  const cssBEM = 'product';
  const messageContext = useNeuroMessages();
  const productCtaUrl = messageContext.getMessage(`${product.id}.cta.url`);
  const productIcon = messageContext.getMessage(`${product.id}.icon`);
  const productFirstCtaUrl = messageContext.getMessage(`${product.id}.cta.url.firstCta`);
  const productSecondCtaUrl = messageContext.getMessage(`${product.id}.cta.url.secondCta`);
  let productName = messageContext.getMessage(`${product.id}.name`).replace(/(<([^>]+)>)/gi, "").replace(/\s/g, "").replace(/[^\w]/g,'');
  productName = productName.toLowerCase()


  const onProductClick = () => {
    onProductActionClick({
      actionName: 'productApplyClicked',
    });
    window.open( productCtaUrl,  "_blank" );
  }

  const renderProductIcon = () => {
    return <div className={`${cssBEM}__icon`}>
      <ReactSVG src={ !productIcon ? '/classpath/assets/images/icons/icon-default.svg' : productIcon } />
    </div>;
  };

  const renderProductName = () => {
    return name ?
    <div className={`${cssBEM}__header`}>
      <h2><a href={ url } target="_blank"><NeuroMessage id={`${product.id}.name`} /></a></h2>
    </div> : null;
  };

  const renderProductDescription = () => {
    return description ?
    <div className={`${cssBEM}__description`}>
      <NeuroMessage id={`${product.id}.description`} tagName="p" />
      { renderProductFeatures() }
    </div> : null;
  };

  const renderProductDescriptionAbreviated = () => {
    return descriptionAbbreviated ?
    <div className={`${cssBEM}__description`}>
      <NeuroMessage id={`${product.id}.description.abbreviated`} tagName="p" />
      { renderProductFeatures() }
    </div> : null;
  };

  const renderProductFeatures = () => {
    return features ?
    <div className={`${cssBEM}__features`}>
      <Collapsible
        classParentString="collapsible"
        className={'product__collapsible'}
        openedClassName={'product__collapsible--addOns'}
        contentInnerClassName="collapsible__content"
        {...(isDetails && {triggerDisabled: true, open: true})}
        trigger={
          <>
            {!isAddOn ? <NeuroMessage id='products.features.title' tagName={`${isDetails ? "span" : "button"}`} className={`${cssBEM}__features--title ${!isDetails ? "btn" : ""}`} /> : "" }
          </>
        }
      >
        <ul>{features.map( (f,i) => <li key={i}>{f}</li>)}</ul>
      </Collapsible>
    </div> : null;
  };

  const renderProductAddOns = () => {
    return (
      product.addOns?.length > 0 && (
        <Collapsible
          classParentString="collapsible"
          className={'product__collapsible'}
          openedClassName={'product__collapsible--addOns'}
          contentInnerClassName="collapsible__content"
          {...(isDetails && {triggerDisabled: true, open: true})}
          trigger={
            <>
              <NeuroMessage id={`product.addOns`} tagName="div" />
              {!isDetails && <i className="fa fa-angle-down"></i>}
            </>
          }
        >
          <div className={`${cssBEM}__addOns`}>
            <ul className={`${cssBEM}__addOns-list`}>
              {product.addOns.map((ele, id) => {
                return isDetails ? <Product product={ele} isAddOn/> : <NeuroMessage id={`${ele.id}.name`} tagName="li" />
              })}
            </ul>
          </div>
        </Collapsible>
      )
    );
  };

  const renderProductDisclosures = () => {
    return disclosures.length > 0 ?
      <div className={`${cssBEM}__disclosures`}>
        <NeuroMessage id='product.disclosure.title'  tagName='h2' className='productDisclosures__title' />
        <ol>{disclosures.map( (f,i) => <li key={i}>{f}</li>)}</ol>
        <NeuroMessage id={`${product.id}.disclosures.subtext`} shouldHideIfNotFound={true} tagName='span' className='productDisclosures__subtext' />
      </div> : null;
  };


  const onProductFirstCtaClick = () => {
    onProductActionClick({
      actionName: 'productApplyClicked',
    });
    window.open( productFirstCtaUrl,  "_blank" );
  }

  const onProductSecondCtaClick = () => {
    onProductActionClick({
      actionName: 'productApplyClicked',
    });
    window.open( productSecondCtaUrl,  "_blank" );
  }

  const renderProductCtaButton = () => {
    // for single CTA
    const productCtaText = <NeuroMessage id={`${product.id}.cta.buttonText`} />

    // for multiple CTAs
    const productFirstCtaText = <NeuroMessage id={`${product.id}.cta.buttonText.firstCta`} />
    const productSecondCtaText = <NeuroMessage id={`${product.id}.cta.buttonText.secondCta`} />

    return messageContext.hasMessage(`${product.id}.cta.buttonText.firstCta`) && messageContext.hasMessage(`${product.id}.cta.buttonText.secondCta`) ?

      <div className={`${cssBEM}__actions ${cssBEM}__actions--two`}>
        <button onClick={ onProductFirstCtaClick }>
          { productFirstCtaText }
        </button>
        <button onClick={ onProductSecondCtaClick }>
          { productSecondCtaText }
        </button>
      </div>

      : productCtaText ?

      <div className={`${cssBEM}__actions`}>
        <button
          onClick={ onProductClick }>
            { productCtaText }
          </button>
      </div>

      : null;
  }

  return (
    product?.groupType !== 'addOns' && (
      <div className={`${cssBEM} ${productName}`} key={product.id}>
        { renderProductIcon() }
        <div className={`${cssBEM}__container`}>
          {isExisting && <NeuroMessage id={`product.existing`} className={`${cssBEM}__existing`} tagName='p' />}
          { renderProductName() }
          <div className={`${cssBEM}__container--details`}>
            { summary ? renderProductDescriptionAbreviated() : renderProductDescription() }
            { renderProductCtaButton() }
          </div>
          {renderProductAddOns()}
        </div>
        { renderProductDisclosures() }
      </div>
    )
  );
};

Product.propTypes = {
  product: PropTypes.object.isRequired
};

export default Product;
